import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type OrgCredential = {
  id: string;
  name: string;
  propertyId: string;
  propertyName: string | null;
  authProvider?: string | null;
  lastLoginSucceededAt?: string | null;
  storageStateS3Key?: string | null;
  sessionValidationUrl?: string | null;
  updatedAt?: string | null;
  isEphemeral?: boolean;
  executionId?: string | null;
  createdAt?: string | null;
  templateId?: string | null;
  environmentId?: string | null;
  environmentName?: string | null;
  hasInbox?: boolean;
};

export function useOrgCredentials(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: ['org-credentials-with-properties'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/org/credentials`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch credentials');
      const data = await r.json();
      return (data.items || []) as OrgCredential[];
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000, // 60 seconds - credentials rarely change
    placeholderData: (previous) => previous, // Avoid loading flash during refetch
  });

  // Lookup map: credentialId → propertyName
  const credentialPropertyMap = React.useMemo(() => {
    const map = new Map<string, string | null>();
    for (const cred of query.data || []) {
      map.set(cred.id, cred.propertyName);
    }
    return map;
  }, [query.data]);

  // Lookup map: credentialId → propertyId
  const credentialPropertyIdMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const cred of query.data || []) {
      map.set(cred.id, cred.propertyId);
    }
    return map;
  }, [query.data]);

  // Group credentials by property
  const credentialGroups = React.useMemo(() => {
    const creds = query.data || [];
    const groups = new Map<string, { propertyName: string; credentials: OrgCredential[] }>();

    for (const cred of creds) {
      const existing = groups.get(cred.propertyId);
      if (existing) {
        existing.credentials.push(cred);
      } else {
        groups.set(cred.propertyId, {
          propertyName: cred.propertyName || 'Unknown Application',
          credentials: [cred],
        });
      }
    }

    // Sort credentials within each group: non-ephemeral first, then ephemeral,
    // each sub-group sorted by createdAt descending
    const sortCredentials = (a: OrgCredential, b: OrgCredential) => {
      const aEph = a.isEphemeral ? 1 : 0;
      const bEph = b.isEphemeral ? 1 : 0;
      if (aEph !== bEph) return aEph - bEph;
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    };

    return Array.from(groups.entries()).map(([propertyId, group]) => ({
      propertyId,
      propertyName: group.propertyName,
      credentials: group.credentials.sort(sortCredentials),
    }));
  }, [query.data]);

  // Flat list of credential options with property context
  const credentialOptions = React.useMemo(() => {
    return credentialGroups.flatMap((group) =>
      group.credentials.map((cred) => ({
        id: cred.id,
        label: cred.name,
        propertyId: group.propertyId,
        propertyName: group.propertyName,
        description: group.propertyName,
        tags:
          cred.lastLoginSucceededAt || cred.storageStateS3Key
            ? [{ label: 'Verified', variant: 'success' as const }]
            : [],
      }))
    );
  }, [credentialGroups]);

  return {
    ...query,
    credentials: query.data || [],
    credentialPropertyMap,
    credentialPropertyIdMap,
    credentialGroups,
    credentialOptions,
  };
}
